import { useFormik } from 'formik';
import React, { useCallback } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import * as Api from '../../../api';
import { TApiOrderDetails } from "../../../api/endpoints/Order";
import HelpTexts from "../../../components/HelpTexts";
import Checkbox from "../../../components/Inputs/Checkbox/Checkbox";
import StartDateField from "../../../components/OrderBrowser/OrderForm/StartDateField";
import Footnote from "../../../components/RealtimeProductConfigurator/Footnote";
import FormError from "../../../components/RealtimeProductConfigurator/FormError";
import OrderPolicy from "../../../policies/OrderPolicy";
import { partialUpdateOrder } from "../../../store/action-creators/orders";
import { TRootState } from '../../../store/reducers/RootReducer';
import { selectUser } from "../../../store/selectors/UserSelectors";
import OrderHelpers from "../../../utils/OrderHelpers";

interface IOrderFormProps {
  onSubmit?: (order: TApiOrderDetails) => void;
  order: TApiOrderDetails
}

interface IOrderEditFormValues {
  name: string;
  start_date: string;
  research: boolean;
}

const EditOrderForm = (props: IOrderFormProps) => {
  const { order, onSubmit } = props;
  const dispatch = useDispatch<ThunkDispatch<TRootState, any, Action>>();
  const user = useSelector(selectUser);

  const handleSubmit = useCallback((values) => {
    const { name, research, start_date } = values;

    return dispatch(partialUpdateOrder(order.id, {
      name,
      start_date,
      ...(OrderPolicy.canRequestResearchLicence(user, order) && { research })
    })).then((order) => onSubmit?.(order));
  }, [dispatch, order, user, onSubmit]);

  const formik = useFormik<IOrderEditFormValues>({
    initialValues: {
      name: order.name,
      start_date: order.start_date || '',
      research: order.research || false
    },
    onSubmit: (values, { setSubmitting, setErrors }) => {
      handleSubmit(values).then(() => {
        setSubmitting(false);
      }).catch(error => {
        setSubmitting(false);

        if (error instanceof Api.ValidationError) {
          const errors = error.data;
          setErrors(errors);
        }
      });
    }
  });

  const onResearchChange = useCallback((checked: boolean) => {
    formik.setFieldValue('research', checked);
  }, [formik]);

  return (
    <Form onSubmit={formik.handleSubmit} className="order-edit-form">
      <Form.Group controlId="orderName">
        <Form.Label>Order Name</Form.Label>
        <Form.Control
          name="name"
          type="text"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.name}
        />
        <FormError message={formik.errors.name} when={formik.touched.name && Boolean(formik.errors.name)} />
      </Form.Group>

      <StartDateField formik={formik} orderCategory={order.category} />

      {
        OrderPolicy.canRequestResearchLicence(user, order) &&
        <Form.Group controlId="research" className="margin-bottom-xs">
          <Checkbox
            name="research"
            value=""
            checked={formik.values.research}
            onChange={onResearchChange}
            label={OrderHelpers.RESEARCH_LICENCE_LABEL_BY_VERSION_MAPPING[order.cost.version].title}
          />

          <Footnote text={OrderHelpers.RESEARCH_LICENCE_LABEL_BY_VERSION_MAPPING[order.cost.version].info} />
        </Form.Group>
      }

      <Button type="submit" disabled={formik.isSubmitting}>
        {formik.isSubmitting ? 'Updating...' : 'Update'}
      </Button>
    </Form>
  );
};

export default EditOrderForm;
